import * as React from 'react';
import { AlertTriangle, Inbox } from 'lucide-react';
import { cn } from '../lib/cn';
import { Button } from './button';

/**
 * The three standard states every data-bearing screen implements (docs/06 §4).
 *
 * Note there is no `<Spinner />` in this package, and that is deliberate: "skeleton placeholders
 * matching final layout, no spinners-only screens". A spinner tells the user nothing about what
 * is coming; a skeleton reserves the layout and prevents the shift when it arrives.
 */

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-skeleton-pulse rounded-md bg-muted', className)}
      {...props}
    />
  );
}

export interface EmptyStateProps {
  title: string;
  /** One sentence. Then a primary action, if there is a sensible one. */
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border px-6 py-16 text-center',
        className,
      )}
    >
      <div className="text-muted-foreground" aria-hidden="true">
        {icon ?? <Inbox className="h-8 w-8" />}
      </div>
      <h2 className="text-base font-semibold">{title}</h2>
      {description && <p className="max-w-md text-sm text-muted-foreground">{description}</p>}
      {action}
    </div>
  );
}

export interface ErrorStateProps {
  title: string;
  description?: string;
  retryLabel?: string;
  onRetry?: () => void;
  className?: string;
}

/** Inline panel with a retry button — never a dead end (docs/06 §4). */
export function ErrorState({ title, description, retryLabel, onRetry, className }: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-6',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-destructive">
        <AlertTriangle className="h-5 w-5" aria-hidden="true" />
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      {onRetry && retryLabel && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * A fixed 4:3 box that reserves its space BEFORE the image loads (docs/06 §10). Without this the
 * catalog grid reflows as each thumbnail arrives, which on a slow regional connection is the
 * whole page jumping under the user's thumb.
 */
export interface ThumbProps {
  src?: string | null;
  alt: string;
  className?: string;
  placeholder?: React.ReactNode;
}

export function Thumb({ src, alt, className, placeholder }: ThumbProps) {
  const [loaded, setLoaded] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  return (
    // `bg-muted` is the reserved box behind the image — the same surface the Skeleton uses, so the
    // placeholder and the thing that replaces it are the same color and nothing flashes.
    <div className={cn('relative aspect-[4/3] w-full overflow-hidden rounded-md bg-muted', className)}>
      {src && !failed ? (
        <>
          {!loaded && <Skeleton className="absolute inset-0 rounded-none" />}
          <img
            src={src}
            alt={alt}
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
            className={cn(
              'h-full w-full object-cover transition-opacity duration-200',
              loaded ? 'opacity-100' : 'opacity-0',
            )}
          />
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center text-muted-foreground">
          {placeholder ?? <Inbox className="h-6 w-6" aria-hidden="true" />}
        </div>
      )}
    </div>
  );
}
